import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { ActionResultBoolean } from '@portal/models/actionResultBoolean';
import type { ActionResultListWorkingCalendarDayResponse } from '@portal/models/actionResultListWorkingCalendarDayResponse';
import type { ActionResultListWorkingDayOfWeekResponse } from '@portal/models/actionResultListWorkingDayOfWeekResponse';
import type { ActionResultLong } from '@portal/models/actionResultLong';
import type { CreateWorkingCalendarDayRequest } from '@portal/models/createWorkingCalendarDayRequest';
import type { CreateWorkingDayOfWeekRequest } from '@portal/models/createWorkingDayOfWeekRequest';
import type { WorkingCalendarDayResponse } from '@portal/models/workingCalendarDayResponse';
import { WorkingDayOfWeekResponse } from '@portal/models/workingDayOfWeekResponse';
import { workingHoursContext } from '@portal/api-endpoints';
import { getHttpParams } from '@portal/core';
import DayOfWeekEnum = WorkingDayOfWeekResponse.DayOfWeekEnum;

@Injectable()
export class WorkingHoursService {
  private readonly http = inject(HttpClient);

  loadWorkingDaysOfWeek(sellerId: number): Observable<WorkingDayOfWeekResponse[]> {
    const params = getHttpParams({ sellerId });

    return this.http
      .get<ActionResultListWorkingDayOfWeekResponse>(workingHoursContext.daysOfWeek, { params })
      .pipe(map(res => res.value));
  }

  createWorkingDayOfWeek(data: CreateWorkingDayOfWeekRequest): Observable<ActionResultLong> {
    return this.http.post<ActionResultLong>(workingHoursContext.daysOfWeek, data);
  }

  updateWorkingDayOfWeek(data: CreateWorkingDayOfWeekRequest): Observable<ActionResultBoolean> {
    return this.http.put<ActionResultBoolean>(workingHoursContext.daysOfWeek, data);
  }

  deleteWorkingDayOfWeek(sellerId: number, dayOfWeek: DayOfWeekEnum): Observable<ActionResultBoolean> {
    const params = getHttpParams({ sellerId, dayOfWeek });

    return this.http.delete<ActionResultBoolean>(workingHoursContext.daysOfWeek, { params });
  }

  loadWorkingCalendarDays(data: {
    sellerId: number;
    from?: string;
    to?: string;
  }): Observable<WorkingCalendarDayResponse[]> {
    const params = getHttpParams(data);

    return this.http
      .get<ActionResultListWorkingCalendarDayResponse>(workingHoursContext.workingCalendarDays, { params })
      .pipe(map(res => res.value));
  }

  createWorkingCalendarDay(data: CreateWorkingCalendarDayRequest): Observable<ActionResultLong> {
    return this.http.post<ActionResultLong>(workingHoursContext.workingCalendarDays, data);
  }

  updateWorkingCalendarDay(data: CreateWorkingCalendarDayRequest): Observable<ActionResultBoolean> {
    return this.http.put<ActionResultBoolean>(workingHoursContext.workingCalendarDays, data);
  }

  deleteWorkingCalendarDay(sellerId: number, workingDate: string): Observable<ActionResultBoolean> {
    const params = getHttpParams({ sellerId, workingDate });

    return this.http.delete<ActionResultBoolean>(workingHoursContext.workingCalendarDays, { params });
  }
}
